import React, {forwardRef, MutableRefObject, useState} from 'react';
import classNames from 'classnames';
import {useCollapse} from 'react-collapsed';
import {Flex, Icon, PriorityIcon, Title} from '@priority-software/priority-style-react';
import {homepageStore} from '../../../../store/HomepageStore';
import {HomepageGroupType} from '../../../../enums';
import {Counter} from '../../../../components/Counter';
import {GroupSettingsDropdown} from '../../../../components/GroupSettingsDropdown/GroupSettingsDropdown';
import {showTilesSearch, showPortlets} from '../../../../actions/dialogs';
import {toggleGroup, updateCollapsedGroupState} from '../../../../actions/collapsedGroups';
import {getGroupTitle} from '../../../../utils';
import {ignoreDragClassName} from '../../../../constants';
import {ModernTooltip} from '../../../../../../components/Common/Tooltip/ModernTooltip';
import texts from '../../../../texts';
import styles from './Container.module.scss';
import {hasNodeIgnoreClassName} from '../../../../../../utils/clickOutsideDefaultHandler';
import {createDataTestId} from '../../../../../../utils/dataTestId';

export interface Props {
    // component props
    children?: React.ReactNode;
    label?: string;
    style?: React.CSSProperties;
    horizontal?: boolean;
    hover?: boolean;
    handleProps?: React.HTMLAttributes<any>;
    placeholder?: boolean;
    count?: number;
    transitionTime?: number;
    componentClasses?: string;

    // group props
    title: string;
    id: string;
    type: HomepageGroupType;
    isHidden?: boolean;
    isDragging?: boolean;
}

export const Container = forwardRef<HTMLDivElement, Props>((props: Props, ref) => {
    const {
        children,
        handleProps,
        hover,
        count,
        placeholder,
        style,
        transitionTime = 550,
        title: groupName,
        isHidden,
        type,
        id,
        componentClasses,
        isDragging
    } = props;
    const isExpanded = !isHidden;
    const [mountChildren, setMountChildren] = useState(isExpanded);
    const displayedTitle = getGroupTitle(groupName);
    const disableDelete = homepageStore.groups.size === 1;

    const {getCollapseProps} = useCollapse({
        isExpanded,
        duration: transitionTime,
        onTransitionStateChange(state) {
            const toggleContainerContent = async (expanded: boolean) => {
                setMountChildren(expanded);
                await updateCollapsedGroupState(id, !expanded);
            };

            if (state === 'expandStart') {
                toggleContainerContent(true);
            } else if (state === 'collapseEnd') {
                toggleContainerContent(false);
            }
        }
    });

    const onMouseDown = (e: React.MouseEvent) => {
        const isIgnored = hasNodeIgnoreClassName(e.target as HTMLElement, ignoreDragClassName);
        if (handleProps.onMouseDown && !isIgnored) {
            handleProps.onMouseDown(e);
        }
    };

    const onTouchStart = (e: React.TouchEvent) => {
        const isIgnored = hasNodeIgnoreClassName(e.target as HTMLElement, ignoreDragClassName);
        if (handleProps.onTouchStart && !isIgnored) {
            handleProps.onTouchStart(e);
        }
    };

    const portletGroup = type === HomepageGroupType.portlet;

    return (
        <div
            ref={ref as MutableRefObject<HTMLDivElement>}
            style={style as React.CSSProperties}
            className={classNames(styles.container, componentClasses, {
                [styles.placeholder]: !!placeholder,
                [styles.hover]: !!hover,
                [styles.draggableStyles]: isDragging
            })}
            id={id}
            {...createDataTestId(`group:${id}`)}
        >
            <div
                className={classNames(styles.header, 'pui-hp-group-header')}
                {...handleProps}
                onMouseDown={onMouseDown}
                onTouchStart={onTouchStart}
            >
                <Flex justifyContent="space-between" alignItems="center">
                    <Flex
                        inline
                        justifyContent="flex-start"
                        alignItems="center"
                        componentClasses={styles.leftToolbar}
                    >
                        <ModernTooltip
                            tooltip={texts.collapse_icon_tooltip}
                            position="top"
                            delay={1000}
                        >
                            <Icon
                                componentClasses={ignoreDragClassName}
                                icon={isExpanded ? PriorityIcon.arrowUp : PriorityIcon.arrowDown}
                                onClick={() => toggleGroup(id, isExpanded)}
                            />
                        </ModernTooltip>

                        <Icon icon={portletGroup ? PriorityIcon.graph : PriorityIcon.gridView} />

                        {type !== HomepageGroupType.portlet ? (
                            <Title componentClasses={styles.title} skin="h5">
                                {displayedTitle}
                            </Title>
                        ) : null}

                        {!isExpanded ? <Counter number={count} /> : null}
                    </Flex>

                    <Flex inline componentClasses={styles.rightToolbar}>
                        {portletGroup ? (
                            <ModernTooltip
                                tooltip={texts.add_widgets_tooltip}
                                position="top-end"
                                delay={1000}
                            >
                                <Icon
                                    componentClasses={ignoreDragClassName}
                                    icon={PriorityIcon.addPortlet}
                                    onClick={showPortlets}
                                />
                            </ModernTooltip>
                        ) : (
                            <ModernTooltip
                                tooltip={texts.add_tile_tooltip}
                                position="top-end"
                                delay={1000}
                            >
                                <Icon
                                    componentClasses={ignoreDragClassName}
                                    icon={PriorityIcon.newAdd}
                                    onClick={() => showTilesSearch(groupName)}
                                />
                            </ModernTooltip>
                        )}

                        <GroupSettingsDropdown
                            groupTitle={groupName}
                            disableDelete={disableDelete}
                            componentClasses={ignoreDragClassName}
                            isPortletGroup={portletGroup}
                        />
                    </Flex>
                </Flex>
            </div>

            {placeholder ? (
                children
            ) : (
                <div
                    {...getCollapseProps()}
                    className={styles.collapsibleBody}
                    {...createDataTestId('collapsible-group-body')}
                >
                    <div className={styles.itemsWrap}>{mountChildren ? children : null}</div>
                </div>
            )}
        </div>
    );
});
